import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootState } from '../reduxStore';
import { hideLoader, showLoader } from './loaderSlice';
import { showErrorSnackbar, showSuccessSnackbar } from './snackbarSlice';
import {
  generateInvoice,
  getInnerInvoicesInnerTable,
  getInvoicesById,
  getInvoicesByPage,
} from '../../ApiServices/InvoicesService';
import { t } from 'i18next';
import { idText } from 'typescript';
import { handleResponse } from '../../ApiServices/ApiHandler';

export const initialState = {
  value: { totalCount: -1, data: [{}] },
  historyValue: { data: [], _id: null },
  loading: false,
  error: null,
  selectedValue: null,
  rowsPerPage: 10,
  pageNo: 0,
  sortBy: '',
  sortDirection: 0,
  filter: {},
};

const invoiceSlice = createSlice({
  name: 'invoice',
  initialState,
  reducers: {
    clearInvoiceState(state: any) {
      state.rowsPerPage = initialState.rowsPerPage;
      state.pageNo = initialState.pageNo;
      state.value = initialState.value;
      state.loading = false;
      state.error = null;
      state.selectedValue = initialState.selectedValue;
    },
  },

  extraReducers: (builder) => {
    builder.addCase(fetchInvoices.fulfilled, (state: any, action: any) => {
      state.error = initialState.error;
      state.loading = initialState.loading;
      state.selectedValue = initialState.selectedValue;
      state.value = action?.payload?.data;
      state.pageNo = action?.meta?.arg?.page;
      state.rowsPerPage = action?.meta?.arg?.limit;
      state.filter = action?.meta?.arg;
    });
    builder.addCase(fetchInvoices.rejected, (state: any, action: any) => {
      state.error = action?.payload?.error?.message;
      state.loading = false;
      state.selectedValue = initialState?.selectedValue;
      state.filter = action?.meta?.arg;
    });
    builder.addCase(
      fetchInvoicesInnerTable.fulfilled,
      (state: any, action: any) => {
        state.error = initialState.error;
        state.loading = initialState.loading;
        state.historyValue = action?.payload?.data;
      }
    );
    builder.addCase(
      fetchInvoicesInnerTable.rejected,
      (state: any, action: any) => {
        state.loading = initialState.loading;
        state.historyValue = initialState.historyValue;
        state.error = action?.payload;
      }
    );
    builder.addCase(fetchInvoicesById.fulfilled, (state: any, action: any) => {
      state.error = initialState.error;
      state.loading = initialState.loading;
      state.selectedValue = action?.payload?.data;
    });
    builder.addCase(fetchInvoicesById.rejected, (state: any, action: any) => {
      state.loading = initialState.loading;
      state.selectedValue = initialState.selectedValue;
      state.error = action?.payload;
    });
  },
});
export const invoiceState = (state: RootState) => state?.invoice;
export const { clearInvoiceState } = invoiceSlice.actions;
export default invoiceSlice.reducer;

export const fetchInvoices = createAsyncThunk(
  'invoice/fetchInvoices',
  async (payload: any, { fulfillWithValue, rejectWithValue, dispatch }) => {
    try {
      dispatch(showLoader({ message: t('FETCHING_INVOICES') }));
      return fulfillWithValue(await getInvoicesByPage(payload));
    } catch (error) {
      dispatch(showErrorSnackbar({ message: error }));
      return rejectWithValue(error);
    } finally {
      dispatch(hideLoader());
    }
  }
);

export const fetchInvoicesInnerTable = createAsyncThunk(
  'invoice/fetchInvoicesInnerTable',
  async (payload: any, { fulfillWithValue, rejectWithValue, dispatch }) => {
    try {
      dispatch(showLoader({ message: t('FETCHING_INVOICE_DETAILS') }));
      let response: any = await getInnerInvoicesInnerTable(payload);
      return fulfillWithValue(response);
    } catch (error) {
      dispatch(showErrorSnackbar({ message: error }));
      return rejectWithValue(error);
    } finally {
      dispatch(hideLoader());
    }
  }
);

export const fetchInvoicesById = createAsyncThunk(
  'invoice/fetchInvoiceById',
  async (payload: any, { fulfillWithValue, rejectWithValue, dispatch }) => {
    try {
      dispatch(showLoader({ message: t('FETCHING_INVOICE_DETAILS') }));
      let response: any = await getInvoicesById(payload);
      return fulfillWithValue(response);
    } catch (error) {
      dispatch(showErrorSnackbar({ message: error }));
      return rejectWithValue(error);
    } finally {
      dispatch(hideLoader());
    }
  }
);

export const createInvoice = createAsyncThunk(
  'invoice/createInvoice',
  async (payload: any, { fulfillWithValue, rejectWithValue, dispatch }) => {
    try {
      dispatch(showLoader({ message: t('CREATING_INVOICE') }));
      let response: any = await generateInvoice(payload);
      if (response?.status === 200 || response === 200) {
        dispatch(
          showSuccessSnackbar({ message: t('INVOICE_CREATED_SUCCESS') })
        );
        return fulfillWithValue(response);
      } else {
        dispatch(showErrorSnackbar({ message: t('INVOICE_CREATION_FAILED') }));
        return rejectWithValue(response);
      }
    } catch (error) {
      dispatch(showErrorSnackbar({ message: error }));
      return rejectWithValue(error);
    } finally {
      dispatch(hideLoader());
    }
  }
);

export const downloadInvoices = createAsyncThunk(
  'orders/downloadInvoices',
  async (payload: any, { fulfillWithValue, rejectWithValue, dispatch }) => {
    try {
      dispatch(showLoader({ message: t('DOWNLOADING_ORDERS') }));
      let response: any = await getInvoicesByPage(payload);
      if (response === 200)
        dispatch(showSuccessSnackbar({ message: t('DOWNLOAD_SUCCESS') }));
      else dispatch(showErrorSnackbar({ message: t('DOWNLOAD_FAILS') }));
      return fulfillWithValue(response);
    } catch (error) {
      dispatch(showErrorSnackbar({ message: error }));
      return rejectWithValue(error);
    } finally {
      dispatch(hideLoader());
    }
  }
);

export const downloadInvoiceDetails = createAsyncThunk(
  'orders/downloadInvoiceDetails',
  async (payload: any, { fulfillWithValue, rejectWithValue, dispatch }) => {
    try {
      dispatch(showLoader({ message: t('DOWNLOADING_INVOICE_DETAILS') }));
      let response: any = await getInvoicesById(payload);
      if (response === 200)
        dispatch(showSuccessSnackbar({ message: t('DOWNLOAD_SUCCESS') }));
      else dispatch(showErrorSnackbar({ message: t('DOWNLOAD_FAILS') }));
      return fulfillWithValue(response);
    } catch (error) {
      dispatch(showErrorSnackbar({ message: error }));
      return rejectWithValue(error);
    } finally {
      dispatch(hideLoader());
    }
  }
);
